import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import WalletFundingPanel from "@/components/WalletFundingPanel";

export const WALLET_IDS = {
  container: "wallet-container",
  balance: "wallet-balance",
  pending: "wallet-pending-cashout",
  ledger: "wallet-ledger",
  ledgerRow: "wallet-ledger-row",
  cashoutForm: "cashout-form",
  cashoutAmount: "cashout-amount-input",
  cashoutDest: "cashout-destination-input",
  cashoutNote: "cashout-note-textarea",
  cashoutSubmit: "cashout-submit-button",
  cashoutsList: "cashouts-list",
  cashoutRow: "cashout-row",
  connectCard: "connect-card",
  connectBtn: "connect-onboard-button",
  connectStatus: "connect-status-badge",
  autoCard: "auto-withdraw-card",
  autoToggle: "auto-withdraw-toggle",
  autoMin: "auto-withdraw-min-input",
  autoSave: "auto-withdraw-save-button",
  topupCard: "topup-card",
  topupAmount: "topup-amount-input",
  topupSubmit: "topup-submit-button",
  interacCard: "interac-deposit-card",
  interacAmount: "interac-amount-input",
  interacSubmit: "interac-submit-button",
  interacReference: "interac-reference-code",
  interacDepositsList: "interac-deposits-list",
  cashoutMethod: "cashout-method-select",
};

const fmt = (c) => {
  const v = (c / 100).toFixed(2);
  return c < 0 ? `-$${(-c / 100).toFixed(2)}` : `$${v}`;
};

const KIND_LABEL = {
  author: "Financier · Author share",
  voter_faculty: "Financier · Voter Faculty share",
  pain_claim: "Pain & Suffering claim",
  alt_financier: "Alternate Financier grant",
  cashout_hold: "Cashout request (hold)",
  cashout_paid: "Cashout · Stripe Connect",
  wallet_topup: "Top-up · Stripe",
  alt_pool_donation: "Donation · Alt Financier pool",
  financier_pledge: "Financier · Pledge (debit)",
};

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("1.00");
  const [destination, setDestination] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("stripe_connect");
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMin, setAutoMin] = useState("5.00");
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    const { data: d } = await api.get("/wallet");
    setData(d);
    if (d?.auto_withdraw) {
      setAutoEnabled(!!d.auto_withdraw.enabled);
      const min = d.auto_withdraw.min_cents ?? d.auto_withdraw.floor_cents ?? 500;
      setAutoMin((min / 100).toFixed(2));
    }
  }, []);
  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    const topup = params.get("topup");
    const sid = params.get("session_id");
    if (topup === "success" && sid) {
      // Reconcile from Stripe side if webhook hasn't landed yet
      api.get(`/payments/status/${sid}`).catch(() => {});
      toast.success("Top-up received · balance will update shortly");
      load();
    } else if (topup === "cancel") {
      toast.message("Top-up cancelled");
    }
    // params is a URLSearchParams; it stays referentially stable between renders
    // but React Hooks lint still asks for load — now safely memoized above.
  }, [params, load]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0b0c10]">
        <Navbar />
        <div className="max-w-xl mx-auto p-10 text-slate-300 text-center">
          Please <Link to="/login" className="underline text-yellow-400">sign in</Link> to view your wallet.
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!cents || cents < 1) return toast.error("Enter an amount");
    let payloadDest = null;
    if (method === "interac") {
      const email = destination.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return toast.error("Enter the Interac recipient email");
      }
      payloadDest = `interac:${email}`;
    } else if (method === "other") {
      if (!destination.trim()) return toast.error("Enter a payout destination");
      payloadDest = destination.trim();
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/wallet/cashout", {
        amount_cents: cents,
        destination: payloadDest,
        note: note.trim() || null,
      });
      toast.success(res.status === "paid" ? "Paid out via Stripe Connect" : "Cashout request submitted · funds held");
      setAmount("1.00");
      setDestination("");
      setNote("");
      await Promise.all([load(), refreshUser()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cashout failed");
    } finally {
      setBusy(false);
    }
  };

  const startConnectOnboarding = async () => {
    setConnecting(true);
    try {
      const { data: res } = await api.post("/wallet/connect/onboard", { return_origin: window.location.origin });
      window.location.href = res.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stripe Connect onboarding failed");
      setConnecting(false);
    }
  };

  const saveAutoWithdraw = async (nextEnabled, nextMinCents) => {
    try {
      await api.post("/wallet/auto-withdraw", {
        enabled: nextEnabled,
        min_cents: nextMinCents,
      });
      toast.success(nextEnabled ? "Auto-withdraw on" : "Auto-withdraw off");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain" data-testid={WALLET_IDS.container}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-5 gap-6">
        <section className="lg:col-span-3 space-y-5">
          <p className="telesis-eyebrow">Wallet</p>
          <h1 className="text-3xl md:text-4xl font-black text-slate-100 tracking-tight">
            Your running CAI balance
          </h1>
          <div className="telesis-panel rounded p-6">
            <p className="telesis-eyebrow">Available</p>
            <p data-testid={WALLET_IDS.balance} className="text-5xl font-black mt-2 text-yellow-400">
              {fmt(data?.balance_cents ?? 0)}
            </p>
            {data?.pending_cashout_cents > 0 && (
              <p data-testid={WALLET_IDS.pending} className="mt-2 text-xs font-mono text-slate-400">
                {fmt(data.pending_cashout_cents)} on hold for pending cashouts
              </p>
            )}
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Credited from Financier splits (author + voter-faculty equal share), Pain &amp; Suffering claims and Alternate Financier grants. Real-money delivery requires a Stripe Connect integration — until then, cashout requests are processed manually.
            </p>
          </div>

          <div className="telesis-panel rounded p-6" data-testid={WALLET_IDS.connectCard}>
            <p className="telesis-eyebrow">Payouts · Stripe Connect</p>
            {data?.connect?.account_id ? (
              <>
                <p className="mt-2 text-sm text-slate-300">
                  Account:{" "}
                  <span className="font-mono text-slate-100">{data.connect.account_id}</span>
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Payouts enabled:{" "}
                  <span data-testid={WALLET_IDS.connectStatus} className={data.connect.payouts_enabled ? "text-emerald-300" : "text-rose-300"}>
                    {String(data.connect.payouts_enabled)}
                  </span>
                </p>
                {!data.connect.payouts_enabled && (
                  <Button
                    data-testid={WALLET_IDS.connectBtn}
                    onClick={startConnectOnboarding}
                    disabled={connecting}
                    className="mt-3 rounded-full bg-yellow-400 text-black hover:bg-yellow-300"
                  >
                    {connecting ? "Opening…" : "Continue onboarding"}
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-300">
                  Connect a Stripe account to receive real-money payouts straight to your bank.
                </p>
                <Button
                  data-testid={WALLET_IDS.connectBtn}
                  onClick={startConnectOnboarding}
                  disabled={connecting}
                  className="mt-3 rounded-full bg-yellow-400 text-black hover:bg-yellow-300"
                >
                  {connecting ? "Opening…" : "Connect Stripe account"}
                </Button>
              </>
            )}
          </div>

          <div className="telesis-panel rounded p-6 space-y-3" data-testid={WALLET_IDS.autoCard}>
            <p className="telesis-eyebrow">Auto-withdraw</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-200">Automatic recurring payouts</p>
                <p className="text-xs text-slate-500">Daily sweep; triggers only when balance ≥ minimum.</p>
              </div>
              <Switch
                data-testid={WALLET_IDS.autoToggle}
                checked={autoEnabled}
                onCheckedChange={(v) => {
                  setAutoEnabled(v);
                  const min = Math.max(
                    data?.auto_withdraw?.floor_cents ?? 500,
                    Math.round(parseFloat(autoMin || "0") * 100)
                  );
                  saveAutoWithdraw(v, min);
                }}
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Minimum (CAD)</Label>
                <Input
                  data-testid={WALLET_IDS.autoMin}
                  type="number"
                  min="5"
                  step="0.5"
                  value={autoMin}
                  onChange={(e) => setAutoMin(e.target.value)}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
                />
              </div>
              <Button
                data-testid={WALLET_IDS.autoSave}
                onClick={() => {
                  const min = Math.max(
                    data?.auto_withdraw?.floor_cents ?? 500,
                    Math.round(parseFloat(autoMin || "0") * 100)
                  );
                  saveAutoWithdraw(autoEnabled, min);
                }}
                variant="outline"
                className="rounded-full border-[#2a2f3f] bg-transparent text-slate-100 hover:bg-[#1a1d28]"
              >
                Save
              </Button>
            </div>
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
              Floor: ${((data?.auto_withdraw?.floor_cents ?? 500) / 100).toFixed(2)} CAD
            </p>
          </div>

          <form data-testid={WALLET_IDS.cashoutForm} onSubmit={submit} className="telesis-panel rounded p-6 space-y-4">
            <p className="telesis-eyebrow">Request a cashout</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Payout method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger
                  data-testid={WALLET_IDS.cashoutMethod}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141c] border-[#222634] text-slate-100">
                  <SelectItem value="stripe_connect">Bank account · Stripe Connect</SelectItem>
                  <SelectItem value="interac">Interac e-Transfer</SelectItem>
                  <SelectItem value="other">Other (PayPal, IBAN, cheque…)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Amount (CAD)</Label>
                <Input
                  data-testid={WALLET_IDS.cashoutAmount}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
                />
              </div>
              {method !== "stripe_connect" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">
                    {method === "interac" ? "Recipient email (Interac)" : "Payout destination"}
                  </Label>
                  <Input
                    data-testid={WALLET_IDS.cashoutDest}
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder={method === "interac" ? "steve@example.com" : "e.g. PayPal: steve@example.com"}
                    className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
                  />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Note (optional)</Label>
              <Textarea
                data-testid={WALLET_IDS.cashoutNote}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
              />
            </div>
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
              {method === "stripe_connect"
                ? "Routed via Stripe Connect to your linked bank. Instant when payouts are enabled."
                : method === "interac"
                ? "Queued for Interac e-Transfer. Processed by staff within 1 business day."
                : "Queued for manual processing. Expect 1-3 business days."}
            </p>
            <Button
              data-testid={WALLET_IDS.cashoutSubmit}
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
            >
              {busy ? "Submitting…" : "Request cashout"}
            </Button>
          </form>

          <WalletFundingPanel onFunded={load} />

          <div data-testid={WALLET_IDS.ledger} className="telesis-panel rounded p-6">
            <p className="telesis-eyebrow mb-3">Ledger · latest 200</p>
            {(!data || data.ledger.length === 0) && (
              <p className="text-xs text-slate-500 font-mono">no entries yet</p>
            )}
            <ul className="divide-y divide-white/5">
              {data?.ledger.map((row) => (
                <li
                  key={row.payout_id}
                  data-testid={WALLET_IDS.ledgerRow}
                  className="py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200 truncate">
                      {KIND_LABEL[row.kind] || row.kind}
                    </p>
                    <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                      {new Date(row.created_at).toLocaleString()}
                      {row.memo ? ` · ${row.memo}` : ""}
                    </p>
                  </div>
                  <span
                    className={`font-mono text-sm ${
                      row.amount_cents < 0 ? "text-rose-300" : "text-emerald-300"
                    }`}
                  >
                    {row.amount_cents < 0 ? "" : "+"}{fmt(row.amount_cents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <aside className="lg:col-span-2 space-y-3" data-testid={WALLET_IDS.cashoutsList}>
          <p className="telesis-eyebrow">Cashout requests</p>
          {(!data || data.cashouts.length === 0) && (
            <p className="text-xs text-slate-500 font-mono">no requests yet</p>
          )}
          {data?.cashouts.map((c) => (
            <div
              key={c.cashout_id}
              data-testid={WALLET_IDS.cashoutRow}
              className="telesis-panel rounded p-4"
            >
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                {new Date(c.created_at).toLocaleDateString()} · {c.status}
              </p>
              <p className="text-lg font-bold mt-1 text-yellow-300">{fmt(c.amount_cents)}</p>
              {c.destination && (
                <p className="text-xs text-slate-300 mt-1 truncate">→ {c.destination}</p>
              )}
              {c.note && <p className="text-xs text-slate-400 mt-1">{c.note}</p>}
            </div>
          ))}
        </aside>
      </main>
    </div>
  );
}
